import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Text, Icon } from '@Sage/hoi-poi-ui';
import EntityLink from 'components/EntityLink';
import { COMPANIES, OPPORTUNITIES } from 'constants/Entities';
import { withEntityDetail } from 'containers/components/EntityDetail/hooks/useEntityDetail';
import OpportunityIcon from 'containers/opportunities/components/icons/OpportunityIcon';
import ImageChip from 'components/chips/ImageChip';
import LabelPopover from 'components/LabelPopover';
import { stopEntityNavigation } from 'utils/entityNavigation';
import { canViewEntity } from 'utils/entityPermissions';
import { getCompanyDetailUrl, getOpportunityDetailUrl } from 'utils/getUrl';
import { formatDecimalByConfig, DECIMAL_TYPES } from 'utils/decimal';
import { getLiteral } from 'utils/getLiteral';

class OpportunityChip extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            isWin: 0,
            isLost: 0,
            amount: 0,
            probability: 0,
            symbolCurrency: '',
            idEnvironment: null,
            idCurrency: null,
        };
    }

    innerClickPopover = (onClickPopover) => {
        return (e) => {
            e.stopPropagation();
            if (!onClickPopover || e.ctrlKey) return true;
            e.preventDefault();
            onClickPopover(e);
            return false;
        };
    };

    onClickCompany = (event, id) => {
        if (!canViewEntity(COMPANIES)) return stopEntityNavigation(event);
        event.stopPropagation();
        const { onClickPopover, openTab } = this.props;
        if (!onClickPopover || event.ctrlKey) return true;
        event.preventDefault();
        openTab({
            entity: COMPANIES,
            id,
            hasCrudInDetail: true,
        });
        return false;
    };

    parseDataForPopover = (id, data) => {
        const { onClickPopover } = this.props;
        let title, subtitle, idOpportunity, opportunityType, company, deleted;

        this.setState({
            isWin: data.isWin ? 1 : 0,
            isLost: data.isLost ? 1 : 0,
            amount: data.amount || 0,
            probability: data.probability || 0,
            symbolCurrency: data.symbolCurrency || '',
            idEnvironment: data.idEnvironment || data.IdEnvironment || null,
            idCurrency: data.idCurrency || data.IdCurrency || null,
        });

        if (data.deleted) deleted = data.deleted;
        if (data.title) title = data.title;
        if (deleted && !title)
            title = data.opportunity || getLiteral('label_placeholder_unamed_record');
        else {
            const titleLabel = data.opportunity || getLiteral('label_placeholder_unamed_record');
            title = (
                <EntityLink
                    entity={OPPORTUNITIES}
                    href={getOpportunityDetailUrl(data.idOpportunity)}
                    onClick={this.innerClickPopover(onClickPopover)}
                    linkProps={{ type: 'subtitle', target: '_blank' }}
                    textProps={{ type: 'subtitle' }}
                >
                    {titleLabel}
                </EntityLink>
            );
        }

        opportunityType = '';
        if (data.typeExpediente)
            opportunityType = (
                <Text type="caption" className="label-popover-subtitle-line">
                    {data.typeExpediente}
                </Text>
            );

        if (data.subtitle) subtitle = data.subtitle;
        if (data.company) company = ` ${data.company}`;
        if (!subtitle && data.company) {
            subtitle = (
                <div className="fm-popover__related-accounts">
                    <Text type="caption">{getLiteral('label_related_accounts')}</Text>
                    <EntityLink
                        entity={COMPANIES}
                        href={getCompanyDetailUrl(data.idCompany)}
                        onClick={(e) => this.onClickCompany(e, data.idCompany)}
                        linkProps={{ type: 'caption', target: '_blank' }}
                        textProps={{ type: 'caption' }}
                    >
                        {`${company} `}
                    </EntityLink>
                    {opportunityType}
                </div>
            );
        } else subtitle = '-';

        return {
            ...data,
            title,
            subtitle,
            idOpportunity,
            deleted,
        };
    };

    renderActions = () => {
        const { amount, probability, isWin, isLost, symbolCurrency, idEnvironment, idCurrency } =
            this.state;

        let classes = ['fm-label-popover__context-popover__info--state'];
        if (isWin) classes.push('fm-label-popover__context-popover__info--win');
        else if (isLost) classes.push('fm-label-popover__context-popover__info--lost');

        return (
            <div className="fm-label-popover__context-popover__info">
                <div className="fm-label-popover__context-popover__info__left text-ellipsis">
                    <span className={classes.join(' ')}>
                        <Text>{getLiteral('label_opportunity_status')}</Text>
                        {` ${probability * 10}%`}
                    </span>
                </div>
                <div className="fm-label-popover__context-popover__info__right text-right">
                    {formatDecimalByConfig({
                        value: amount,
                        type: DECIMAL_TYPES.CURRENCY_GENERAL,
                        idEnvironment,
                        idCurrency,
                        entityName: OPPORTUNITIES.extraFieldName,
                        fieldId: 'dblvalor',
                        currencySymbol: symbolCurrency,
                    }) || ''}
                </div>
            </div>
        );
    };

    renderPopover(label) {
        const { idOpportunity, popover, remotePopover, classNamePopper, disableClick, noCache } =
            this.props;
        let { isWin, isLost } = this.state;

        return (
            <LabelPopover
                image={<OpportunityIcon won={isWin} lost={isLost} size="large" />}
                id={idOpportunity}
                type="opportunity"
                data={popover}
                remotePopover={remotePopover}
                parseDataForPopover={this.parseDataForPopover}
                renderActions={this.renderActions}
                classNamePopper={classNamePopper}
                disableClick={disableClick}
                noCache={noCache}
            >
                {label}
            </LabelPopover>
        );
    }

    renderChip() {
        const {
            nameOpportunity,
            onClick,
            onMouseEnter,
            onMouseLeave,
            isDismissible,
            onClickDismissible,
        } = this.props;

        let image = <Icon name="opportunities" />;
        let classes = ['fm-opportunity-chip'];

        return (
            <ImageChip
                text={nameOpportunity}
                image={image}
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                classNames={classes}
                isDismissible={isDismissible}
                onClickDismissible={onClickDismissible}
            />
        );
    }

    render() {
        const { children, popover, remotePopover } = this.props;
        let el = null;
        let chipEl = children || this.renderChip();

        if ((popover && popover.title) || remotePopover) el = this.renderPopover(chipEl);
        else el = this.renderChip();
        return el;
    }
}

OpportunityChip.propTypes = {
    idOpportunity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    nameOpportunity: PropTypes.string,
    remotePopover: PropTypes.bool,
};

export default withEntityDetail(OpportunityChip);
